/**
 * Every status transition of the machines of a line since the shift start,
 * then the new ones as they arrive.
 *
 * The <ORG>_SHIFT stream keeps up to 1000 messages per subject for 7 days, so
 * the shift's transitions are still there. One ordered consumer for the whole
 * line reads them (a filter subject per machine, delivered from the shift
 * start, no acks), rather than one consumer per machine: consumer churn on
 * that stream once amplified an outage (see liveShift.worker.ts).
 */

import { useEffect, useRef, useState } from "react";
import { consumerOpts } from "nats.ws";
import { getValidToken } from "../../auth/tokenManager";
import type { LiveStatusTransition, StatusTransition } from "../../hooks/useLiveShiftData";
import { getActiveOrgAlias } from "../../services/demoMode";
import { natsClient } from "../../services/NATSClient";
import { getShiftStreamName } from "../../utils/tokenUtils";

const NOT_CONNECTED_RETRY_MS = 2000;
/** State updates while the history replays, at most this often. */
const FLUSH_MS = 1000;

export interface StatusHistory {
  /** Transitions per machine (sn), oldest first. */
  transitions: Map<string, StatusTransition[]>;
  /** The history up to now has arrived. */
  ready: boolean;
  /** The history could not be read (the line view then has no flow analysis). */
  failed: boolean;
}

const EMPTY: StatusHistory = { transitions: new Map(), ready: false, failed: false };

export function useLineStatusHistory(sns: string[], startMs: number | null): StatusHistory {
  const [state, setState] = useState<StatusHistory>(EMPTY);
  const bufferRef = useRef<Map<string, StatusTransition[]>>(new Map());
  const snsKey = sns.join(",");

  useEffect(() => {
    const list = snsKey ? snsKey.split(",") : [];
    setState(EMPTY);
    bufferRef.current = new Map();
    if (!startMs || list.length === 0) return;

    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let flush: ReturnType<typeof setInterval> | null = null;
    let sub: { unsubscribe(): void } | null = null;
    let ready = false;
    let dirty = false;
    // edge serials are zero-padded to 5 digits; the line's machines may not be
    const snOf = new Map(list.map((sn) => [sn.padStart(5, "0"), sn]));

    const publish = () => {
      if (cancelled || !dirty) return;
      dirty = false;
      setState({
        transitions: new Map([...bufferRef.current].map(([sn, trs]) => [sn, [...trs]])),
        ready,
        failed: false,
      });
    };

    const start = async () => {
      if (cancelled) return;
      if (!natsClient.isConnected()) {
        retry = setTimeout(() => void start(), NOT_CONNECTED_RETRY_MS);
        return;
      }
      try {
        const alias = getActiveOrgAlias(await getValidToken());
        if (!alias || cancelled) return;
        const subjects = [...snOf.keys()].map((serial) => `edge-data.${serial}.shift.current.status.transition`);
        const opts = consumerOpts();
        opts.orderedConsumer();
        for (const s of subjects) opts.filterSubject(s);
        opts.startTime(new Date(startMs));
        opts.ackNone();
        (opts as unknown as { stream: string }).stream = getShiftStreamName(alias);

        const js = natsClient.jetstream();
        const s = await js.subscribe(subjects[0], opts);
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
        flush = setInterval(publish, FLUSH_MS);

        for await (const m of s) {
          if (cancelled) break;
          const serial = m.subject.split(".")[1] ?? "";
          const sn = snOf.get(serial);
          try {
            const msg = JSON.parse(new TextDecoder().decode(m.data)) as LiveStatusTransition;
            if (sn && msg?.transition?.at) {
              const trs = bufferRef.current.get(sn) ?? [];
              const prev = trs[trs.length - 1];
              // a repeated message of the same transition changes nothing
              if (!prev || prev.at !== msg.transition.at || prev.to !== msg.transition.to) {
                trs.push(msg.transition);
                bufferRef.current.set(sn, trs);
                dirty = true;
              }
            }
          } catch {
            // not a transition message
          }
          if (!ready && (m.info?.pending ?? 0) === 0) {
            ready = true;
            dirty = true;
            publish();
          }
        }
      } catch (err) {
        console.warn("[useLineStatusHistory] could not read the status history:", err);
        if (!cancelled) setState({ transitions: new Map(), ready: true, failed: true });
      }
    };

    void start();
    // a shift without any transition yet: nothing to replay, the history is complete
    const emptyCheck = setTimeout(() => {
      if (!cancelled && !ready && bufferRef.current.size === 0 && sub) {
        ready = true;
        dirty = true;
        publish();
      }
    }, 5000);

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      if (flush) clearInterval(flush);
      clearTimeout(emptyCheck);
      try {
        sub?.unsubscribe();
      } catch {
        // already closed
      }
    };
  }, [snsKey, startMs]);

  return state;
}
